import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridToolbar,
} from "@progress/kendo-react-grid";
import CustomButton from "../../../components/Button";
import { DeleteIcon } from "../../../assets/Icons/DeleteIcon";
import {
  adhocTables,
  calculateRowTotals,
  calculateTotalDue,
  calculationDependentFields,
  creditTables,
  newRowData,
  nonEditableFields,
  textFields,
  validateGridData,
} from "./util";
import { ErrorObj, TableItem } from "./types";
import { Alert, Box } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useState } from "react";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import React from "react";
import { DropDownCell } from "./DropdownCell";

interface Props {
  sectionName:
    | "applicationProductTableData"
    | "applicationOperationTableData"
    | "harvestTableData"
    | "tillageTableData"
    | "seedingTableData"
    | "plantingTableData"
    | "adhocProducts"
    | "creditTableData";
  tableData: any[];
  headers: { [key: string]: string };
  setTableData: (data: any[]) => void;
  totalDue: number;
  setTotalDue: (total: number) => void;
  setErrorFieldsObj: React.Dispatch<React.SetStateAction<ErrorObj>>;
  validateFn?: (data: any[], headers: { [key: string]: string }) => string[];
  columnWidths?: { [key: string]: number };
  totalLabel?: string;
}

type ActionProps = GridCustomCellProps & { onDelete: (dataItem: any) => void };

const ActionCell = (props: ActionProps) => {
  const { onDelete, dataItem } = props;
  return (
    <td
      {...props.tdProps}
      onClick={() => onDelete(dataItem)}
      style={{ cursor: "pointer" }}
    >
      <DeleteIcon />
    </td>
  );
};

export const EditTable: React.FC<Props> = ({
  sectionName,
  headers,
  tableData,
  setTableData,
  totalDue,
  setTotalDue,
  setErrorFieldsObj,
  validateFn = validateGridData,
  columnWidths = {},
  totalLabel = "Total Due",
}) => {
  const [errorFields, setErroFields] = useState<string[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});

  const onItemchange = (e: any) => {
    const { field, value, dataItem } = e;
    const updatedData = tableData.map((item: any) => {
      if (item.id === dataItem.id) {
        const updatedRow = {
          ...item,
        };
        updatedRow[field] = value;
        if (
          (adhocTables.includes(sectionName) && field === "totalCost") ||
          calculationDependentFields.includes(field)
        ) {
          calculateRowTotals(updatedRow, sectionName);
        }
        return updatedRow;
      }
      return item;
    });
    setTableData(updatedData);
  };

  const addNewRow = () => {
    setTableData([...tableData, { ...newRowData[sectionName], id: uuidv4() }]);
  };

  const deleteRow = (dataItem: TableItem) => {
    setTableData(
      tableData.filter((item: TableItem) => item.id !== dataItem.id)
    );
  };

  useEffect(() => {
    const errors = validateFn(tableData, headers);
    setErroFields(errors);
    setErrorFieldsObj((prev: ErrorObj) => {
      return { ...prev, [sectionName]: errors };
    });
    setTotalDue(calculateTotalDue(tableData));
    setEdit(
      tableData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [tableData]);

  return (
    <>
      {errorFields.length > 0 && (
        <Alert severity="warning" sx={{ marginBottom: 1 }}>
          {`${errorFields.join(", ")} cannot be empty`}
        </Alert>
      )}
      <Grid
        data={tableData}
        editable={true}
        dataItemKey="id"
        onItemChange={onItemchange}
        edit={edit}
      >
        <GridToolbar>
          <CustomButton title="Add new" variant="contained" onClick={addNewRow}>
            Add Row
          </CustomButton>
        </GridToolbar>
        {Object.keys(headers).map((field: string) => {
          if (field === "id") {
            return (
              <GridColumn
                key={field}
                field="id"
                hidden={true}
                title={headers[field]}
                editable={false}
                width={200}
                className="centered-column"
                headerClassName="centered-header"
              />
            );
          } else if (field === "fieldType") {
            return (
              <GridColumn
                key={field}
                field="fieldType"
                title="Field"
                editor="text"
                editable={true}
                cells={{data: DropDownCell}}
                width={columnWidths[field] ?? 150}
                className="centered-column"
                headerClassName="centered-header"
              />
            );
          } else if (field === "cropType") {
            return (
              <GridColumn
                key={field}
                field="cropType"
                title="Crop"
                editor="text"
                editable={true}
                cells={{data: DropDownCell}}
                width={columnWidths[field] ?? 150}
                className="centered-column"
                headerClassName="centered-header"
              />
            );
          } else if (field === "date") {
            return (
              <GridColumn
                key={field}
                field="date"
                title="Date"
                editor="date"
                editable={true}
                format={"{0:d}"}
                width={columnWidths[field] ?? 150}
                className="centered-column"
                headerClassName="centered-header"
              />
            );
          }
          return (
            <GridColumn
              key={field}
              field={field}
              title={headers[field]}
              editable={
                (adhocTables.includes(sectionName) && field === "totalCost") ||
                (creditTables.includes(sectionName) && field === "totalDue") ||
                !nonEditableFields.includes(field)
              }
              editor={textFields.includes(field) ? "text" : "numeric"}
              width={columnWidths[field] ?? 120}
              className="centered-column"
              headerClassName="centered-header"
            />
          );
        })}

        <GridColumn
          cells={{data: (props) => <ActionCell {...props} onDelete={deleteRow} />}}
          width={50}
          headerClassName="centered-header"
        />
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "right", gap: "1rem" }}>
        <strong>{totalLabel}: {totalDue}</strong>
      </Box>
    </>
  );
};
